import * as Yup from 'yup';
import i18next from '../i18n';
import { formatMoneyNumeral } from '../util/helpers/numeralHelpers';

const MINIMUM_WITHDRAWAL_AMOUNT = 0.01;
const MINIMUM_CREDIT_AMOUNT = 0;

const safeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return Number.isNaN(num) ? defaultValue : num;
};

const createNullableTransform = (value, originalValue) =>
  originalValue === '' || originalValue == null ? null : value;

const createAmountValidation = (
  minAmount,
  maxAmount,
  minErrorKey = 'validationSchema.withdrawal.withdrawalMinAmount',
) =>
  Yup.number()
    .required(i18next.t('validationSchema.general.amountRequired'))
    .nullable()
    .transform(createNullableTransform)
    .min(
      minAmount,
      i18next.t(minErrorKey, { amount: formatMoneyNumeral(minAmount) }),
    )
    .max(maxAmount, i18next.t('validationSchema.general.insufficientFunds'));

const calculateMaxWithdrawalAmount = (
  availableBalance,
  wireWithdrawalFee,
  wireWithdrawalFeeCredit,
) => {
  const balance = safeNumber(availableBalance);
  const fee = safeNumber(wireWithdrawalFee);
  const credit = safeNumber(wireWithdrawalFeeCredit);

  return Math.max(0, balance - fee + credit);
};

export const wireWithdrawalValidationSchema = (
  isImpersonating,
  availableBalance,
  wireWithdrawalFee,
  formValues = {},
) => {
  if (typeof isImpersonating !== 'boolean') {
    throw new Error('isImpersonating must be a boolean');
  }

  if (availableBalance == null || Number.isNaN(Number(availableBalance))) {
    throw new Error('availableBalance must be a valid number');
  }

  const wireWithdrawalFeeCreditAmount = safeNumber(
    formValues.wireWithdrawalFeeCredit,
  );
  const maxAmount = calculateMaxWithdrawalAmount(
    availableBalance,
    wireWithdrawalFee,
    wireWithdrawalFeeCreditAmount,
  );

  const baseSchema = {
    amount: createAmountValidation(MINIMUM_WITHDRAWAL_AMOUNT, maxAmount),
  };

  if (isImpersonating) {
    if (wireWithdrawalFee == null || Number.isNaN(Number(wireWithdrawalFee))) {
      throw new Error(
        'wireWithdrawalFee must be a valid number when isImpersonating is true',
      );
    }

    const maxFeeCredit = safeNumber(wireWithdrawalFee);

    return Yup.object().shape({
      ...baseSchema,
      wireWithdrawalFeeCredit: Yup.number()
        .required(
          i18next.t(
            'validationSchema.withdrawal.wireWithdrawalFeeCreditRequired',
          ),
        )
        .nullable()
        .transform(createNullableTransform)
        .min(
          MINIMUM_CREDIT_AMOUNT,
          i18next.t('validationSchema.withdrawal.withdrawalMinAmount', {
            amount: formatMoneyNumeral(MINIMUM_CREDIT_AMOUNT),
          }),
        )
        .max(
          maxFeeCredit,
          i18next.t('validationSchema.general.insufficientFunds'),
        ),
    });
  }

  return Yup.object().shape(baseSchema);
};
